/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { useCreateDataTransferMutation, useListSubConnectionsQuery } from '@api/initiative-app-integrations';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { CTAdminBreadcrumbs } from '../../../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';
import { SelectFactory, SelectTypes, type Option } from '@g17eco/molecules/select/SelectFactory';
import { SubsidiaryGroupSelect } from '@g17eco/molecules/select/SubsidiaryGroupSelect';
import type { SubConnection } from '@g17eco/types/app-integration';
import { DataPeriods } from '@utils/dataPeriods';
import { getErrorMessage } from '@utils/error';
import { getSubsidiaryLabel } from './utils';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const getLastDayOfMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

const toDateString = (year: number, month: number, day: number): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;


const FormRow = ({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) => (
  <div className='row mb-4 align-items-center'>
    <div className='col-md-4'>
      <div className='fw-bold'>{label}</div>
      {description && <div className='small text-ThemeTextLight'>{description}</div>}
    </div>
    <div className='col-md-8'>{children}</div>
  </div>
);

const MONTH_OPTIONS: Option<string>[] = MONTHS.map((m, i) => ({ value: String(i + 1), label: m }));
const YEAR_OPTIONS: Option<string>[] = YEARS.map((y) => ({ value: String(y), label: String(y) }));

const findOption = (options: Option<string>[], value: string) => options.find((o) => o.value === value) ?? null;

const MonthYearSelect = ({ month, year, onMonthChange, onYearChange }: {
  month: number; year: number;
  onMonthChange: (m: number) => void; onYearChange: (y: number) => void;
}) => (
  <div className='d-flex gap-2'>
    <SelectFactory
      selectType={SelectTypes.SingleSelect}
      options={MONTH_OPTIONS}
      value={findOption(MONTH_OPTIONS, String(month))}
      onChange={(option) => { if (option) { onMonthChange(Number(option.value)); } }}
      size='sm'
      minWidth={90}
    />
    <SelectFactory
      selectType={SelectTypes.SingleSelect}
      options={YEAR_OPTIONS}
      value={findOption(YEAR_OPTIONS, String(year))}
      onChange={(option) => { if (option) { onYearChange(Number(option.value)); } }}
      size='sm'
      minWidth={80}
    />
  </div>
);

const toSubsidiaryItems = (subConnections: SubConnection[]) =>
  subConnections.map((sc) => ({
    _id: sc.initiativeId,
    name: getSubsidiaryLabel(sc),
  }));

export const DataTransferCreateForm = () => {
  const { code, initiativeId } = useParams<{ code: string; initiativeId: string }>();
  const isUserStaff = useAppSelector(isStaff);
  const history = useHistory();

  const subConnectionsQuery = useListSubConnectionsQuery({ initiativeId, code }, { skip: !isUserStaff });
  const [createMutation, { isLoading }] = useCreateDataTransferMutation();

  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<DataPeriods>(DataPeriods.Yearly);
  const [year, setYear] = useState(CURRENT_YEAR - 1);
  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear] = useState(CURRENT_YEAR - 1);
  const [endMonth, setEndMonth] = useState(12);
  const [endYear, setEndYear] = useState(CURRENT_YEAR - 1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string>();

  if (!isUserStaff) {
    return null;
  }

  const getDateRange = (): { startDate: string; endDate: string } => {
    if (frequency === DataPeriods.Yearly) {
      return { startDate: toDateString(year, 1, 1), endDate: toDateString(year, 12, 31) };
    }
    return {
      startDate: toDateString(startYear, startMonth, 1),
      endDate: toDateString(endYear, endMonth, getLastDayOfMonth(endYear, endMonth)),
    };
  };

  const getAutoName = (): string => {
    if (frequency === DataPeriods.Yearly) {
      return `${year} Annual Sync`;
    }
    return `${MONTHS[startMonth - 1]} ${startYear} – ${MONTHS[endMonth - 1]} ${endYear} Monthly Sync`;
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one subsidiary.');
      return;
    }
    setError(undefined);
    const { startDate, endDate } = getDateRange();
    const result = await createMutation({
      initiativeId,
      code,
      name: name || getAutoName(),
      startDate,
      endDate,
      frequency,
      childInitiativeIds: Array.from(selectedIds),
    });
    if ('data' in result && result.data) {
      history.push(generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFER_SYNC, {
        initiativeId, code, dataTransferId: result.data._id,
      }));
    } else if ('error' in result) {
      setError(getErrorMessage(result.error));
    }
  };

  return (
    <Dashboard className='w-100'>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: code, url: generateUrl(ROUTES.APP_INTEGRATIONS_VIEW, { initiativeId, code }) },
            { label: 'Data Sync', url: generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFERS, { initiativeId, code }) },
            { label: 'New' },
          ]}
        />
      </div>
      <DashboardSectionTitle title={<>Configure New Data Sync <FeatureStability stability='internal' /></>} />
      <DashboardSection>
        <div style={{ maxWidth: 640 }}>
          <FormRow label='Transfer Name'>
            <input
              className='form-control'
              placeholder={getAutoName()}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormRow>

          <FormRow label='Subsidiary Selection' description='Choose subsidiaries for data sync.'>
            <QueryWrapper query={subConnectionsQuery} onSuccess={(subConnections) => (
              <SubsidiaryGroupSelect
                initiativeId={initiativeId}
                subsidiaries={toSubsidiaryItems(subConnections)}
                selectedSubsidiaryIds={Array.from(selectedIds)}
                onChange={(ids) => setSelectedIds(new Set(ids))}
                placeholder='Select subsidiaries...'
              />
            )} />
          </FormRow>

          <FormRow label='Date Range' description='Specify the period for comparative analysis.'>
            {frequency === DataPeriods.Yearly ? (
              <SelectFactory
                selectType={SelectTypes.SingleSelect}
                options={YEAR_OPTIONS}
                value={findOption(YEAR_OPTIONS, String(year))}
                onChange={(option) => { if (option) { setYear(Number(option.value)); } }}
                minWidth={100}
              />
            ) : (
              <div className='d-flex align-items-center gap-3'>
                <div>
                  <div className='small text-ThemeTextLight mb-1'>From</div>
                  <MonthYearSelect
                    month={startMonth} year={startYear}
                    onMonthChange={setStartMonth} onYearChange={setStartYear}
                  />
                </div>
                <div>
                  <div className='small text-ThemeTextLight mb-1'>To</div>
                  <MonthYearSelect
                    month={endMonth} year={endYear}
                    onMonthChange={setEndMonth} onYearChange={setEndYear}
                  />
                </div>
              </div>
            )}
          </FormRow>

          <FormRow label='Frequency'>
            <div className='btn-group'>
              {([DataPeriods.Monthly, DataPeriods.Yearly] as const).map((f) => (
                <button
                  key={f}
                  type='button'
                  className={`btn ${frequency === f ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setFrequency(f)}
                >
                  {f === DataPeriods.Monthly ? 'Monthly' : 'Annual'}
                </button>
              ))}
            </div>
          </FormRow>

          {error && (
            <div className='p-3 mb-3 border rounded border-ThemeDangerMedium text-ThemeDangerMedium background-ThemeDangerExtralight'>
              <i className='fa fa-exclamation-circle me-2' />{error}
            </div>
          )}

          <div className='d-flex justify-content-center gap-2 mt-4'>
            <button
              type='button'
              className='btn btn-primary px-4'
              onClick={handleSubmit}
              disabled={isLoading || selectedIds.size === 0}
            >
              {isLoading ? <><i className='fa fa-spinner fa-spin me-1' />Generating...</> : 'Run Comparison'}
            </button>
            <button
              type='button'
              className='btn btn-outline-secondary px-4'
              onClick={() => history.goBack()}
            >
              Cancel
            </button>
          </div>
        </div>
      </DashboardSection>
    </Dashboard>
  );
};
